import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import {
    createRegulation,
    getRegulationById,
    updateRegulation,
} from '@/modules/Regulations/services'
import {
    AttachmentPayload,
    RegulationPayload,
    RegulationsConfig,
} from '@/modules/Regulations/types'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { renderFileItem } from '@/utils/function'
import dayjs from 'dayjs'
import { FileText, Loader2, Save, X } from 'lucide-react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, GridHookForm } from 'vnetwork-ui'

export interface CreateEditRegulationModalProps extends ModalProps {
    regulation?: RegulationsConfig | null
}

const REGULATION_TYPES = [
    { value: 'company_policy', label: 'Company Policy' },
    { value: 'terms_of_service', label: 'Terms of Service' },
    { value: 'privacy_policy', label: 'Privacy Policy' },
    { value: 'code_of_conduct', label: 'Code of Conduct' },
]

interface FormData {
    regulation_version: string
    regulation_title: string
    regulation_content?: string
    regulation_summary?: string
    regulation_type: string
    is_active: boolean
    is_mandatory: boolean
    effective_date: string
    expired_date?: string
    file?: File[]
}

const CreateEditRegulationModal: React.FC<CreateEditRegulationModalProps> = ({
    regulation,
    onClose,
    open,
}) => {
    const { t } = useTranslation()
    const isEdit = !!regulation

    // Fetch regulation with attachments if editing
    const { data: regulationDetail, isLoading: isLoadingDetail } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.REGULATIONS,
                'regulation-detail',
                regulation?.regulation_id,
            ],
            queryFn: () => getRegulationById(regulation!.regulation_id),
            enabled: isEdit && !!regulation?.regulation_id,
        })

    const existingAttachments = regulationDetail?.result?.attachments || []

    const [removedAttachmentIds, setRemovedAttachmentIds] = React.useState<
        number[]
    >([])

    const {
        control,
        handleSubmit,
        watch,
        register,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues: regulation
            ? {
                  regulation_version: regulation.regulation_version,
                  regulation_title: regulation.regulation_title,
                  regulation_content: regulation.regulation_content || '',
                  regulation_summary: regulation.regulation_summary,
                  regulation_type: regulation.regulation_type,
                  is_active: regulation.is_active,
                  is_mandatory: regulation.is_mandatory,
                  effective_date: regulation.effective_date,
                  expired_date: regulation.expired_date || undefined,
                  file: [],
              }
            : {
                  regulation_version: '',
                  regulation_title: '',
                  regulation_content: '',
                  regulation_summary: '',
                  regulation_type: 'company_policy',
                  is_active: false,
                  is_mandatory: true,
                  effective_date: dayjs().toISOString(),
                  expired_date: undefined,
                  file: [],
              },
    })
    const { uploadAsync, isUploading } = useUploadFile({
        prefix: STORGE_PREFIXS.REGULATIONS,
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: async (data: FormData) => {
                const attachmentsPayload: AttachmentPayload[] = []

                // Upload new files if selected
                if (data.file && data.file.length > 0) {
                    const attachmentUploadeds = await uploadAsync(data.file)
                    const uploadedAttachments = attachmentUploadeds
                        ?.filter(
                            (attachment) => attachment.status === 'fulfilled'
                        )
                        .map((attachment) => attachment.value) as Attachment[]

                    if (uploadedAttachments && uploadedAttachments.length > 0) {
                        uploadedAttachments.forEach((att) => {
                            attachmentsPayload.push({
                                attachment_file_name:
                                    att.attachment_file_name || '',
                                attachment_file_type:
                                    att.attachment_file_type ||
                                    'application/pdf',
                                attachment_file_url:
                                    att.attachment_file_url || '',
                                attachment_file_size:
                                    att.attachment_file_size || 0,
                                attachment_file_note: 'Regulation document',
                            })
                        })
                    }
                }

                // Check if we have at least one attachment (existing or new)
                const remainingExisting = existingAttachments.filter(
                    (att: Attachment) =>
                        !removedAttachmentIds.includes(att.attachment_id)
                )
                if (
                    attachmentsPayload.length === 0 &&
                    remainingExisting.length === 0
                ) {
                    throw new Error(t('regulations:regulation-file-required'))
                }

                const payload: RegulationPayload = {
                    regulation_version: data.regulation_version,
                    regulation_title: data.regulation_title,
                    regulation_content: data.regulation_content || '',
                    regulation_summary: data.regulation_summary,
                    regulation_type: data.regulation_type,
                    is_active: data.is_active,
                    is_mandatory: data.is_mandatory,
                    effective_date: data.effective_date,
                    expired_date: data.expired_date || null,
                    attachments:
                        attachmentsPayload.length > 0
                            ? attachmentsPayload
                            : undefined,
                    remove_attachments:
                        removedAttachmentIds.length > 0
                            ? removedAttachmentIds
                            : undefined,
                }

                if (isEdit && regulation) {
                    return updateRegulation(regulation.regulation_id, payload)
                }
                return createRegulation(payload)
            },
        },
        {
            successMessage: isEdit
                ? t('regulations:regulations-updated-success')
                : t('regulations:regulations-created-success'),
            reactQueryKeys: [
                QUERY_KEYS.REGULATIONS_LIST,
                QUERY_KEYS.REGULATIONS_STATS,
                QUERY_KEYS.REGULATIONS,
            ],
            onSuccess: () => {
                onClose()
            },
        }
    )

    const handleRemoveAttachment = (attachmentId: number) => {
        setRemovedAttachmentIds((prev) => [...prev, attachmentId])
    }

    const isLoading = isPending
    const displayedAttachments = existingAttachments.filter(
        (att: Attachment) => !removedAttachmentIds.includes(att.attachment_id)
    )

    if (isEdit && isLoadingDetail) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                title={t('loading')}
                maxWidth="lg"
            >
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </Dialog>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                isEdit
                    ? t('regulations:edit-regulation')
                    : t('regulations:create-regulation')
            }
            maxWidth="lg"
        >
            <form
                onSubmit={handleSubmit((data) => mutateAsync(data))}
                className="space-y-4"
            >
                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                    <FormItem
                        name="regulation_version"
                        type="textfield"
                        control={control}
                        label={t('version')}
                        placeholder="1.0.0"
                        disabled={isEdit}
                    />
                    <FormItem
                        name="regulation_type"
                        type="select"
                        control={control}
                        label={t('type')}
                        options={REGULATION_TYPES}
                    />
                </div>

                <FormItem
                    name="regulation_title"
                    type="textfield"
                    control={control}
                    label={t('title')}
                    placeholder={t('regulations:regulation-title-placeholder')}
                />

                <FormItem
                    name="regulation_summary"
                    type="textarea"
                    control={control}
                    label={t('summary')}
                    placeholder={t(
                        'regulations:regulation-summary-placeholder'
                    )}
                />

                <Separator />

                {/* File Upload */}
                <div className="space-y-3">
                    <Label className="text-sm font-medium">
                        {t('regulations:regulation-file')}{' '}
                        <span className="text-red-500">*</span>
                    </Label>

                    {/* Existing Attachments */}
                    {displayedAttachments.length > 0 && (
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">
                                {t('current-file')}:
                            </Label>
                            {displayedAttachments?.map((att: Attachment) => (
                                <div
                                    key={att.attachment_id}
                                    className="p-3 border rounded-md bg-muted/10 flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-primary" />
                                        <a
                                            href={att.attachment_file_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-primary hover:underline"
                                        >
                                            {att.attachment_file_name}
                                        </a>
                                        <span className="text-xs text-muted-foreground">
                                            (
                                            {(
                                                att.attachment_file_size / 1024
                                            ).toFixed(1)}{' '}
                                            KB)
                                        </span>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        onClick={() =>
                                            handleRemoveAttachment(
                                                att.attachment_id
                                            )
                                        }
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload New File */}
                    <GridHookForm
                        formControl={
                            {
                                control,
                                handleSubmit,
                                watch,
                                formState: { errors },
                                register,
                            } as any
                        }
                        subForm
                        actions={[]}
                        data={[
                            {
                                formItemProps: {
                                    name: 'file',
                                    type: 'files',
                                    renderFileItem: renderFileItem,
                                    multiple: false,
                                    label:
                                        displayedAttachments.length > 0
                                            ? t('upload-new-file')
                                            : t(
                                                  'regulations:upload-regulation-file'
                                              ),
                                    placeholder: t(
                                        'upload-regulation-file-hint'
                                    ),
                                },
                                className: 'col-span-4',
                            },
                        ]}
                    />
                </div>

                <Separator />

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                    <FormItem
                        name="effective_date"
                        type="datepicker"
                        control={control}
                        label={t('effective-date')}
                    />
                    <FormItem
                        name="expired_date"
                        type="datepicker"
                        control={control}
                        label={t('expired-date')}
                        placeholder={t('optional')}
                    />
                </div>

                <Separator />

                {/* Options */}
                <div className="flex items-center gap-6">
                    <FormItem
                        name="is_mandatory"
                        type="checkbox"
                        control={control}
                        label={t('mandatory-acceptance')}
                    />
                    <FormItem
                        name="is_active"
                        type="checkbox"
                        control={control}
                        label={t('set-as-active')}
                    />
                </div>

                <p className="text-xs text-muted-foreground">
                    {t('regulations:regulation-active-note')}
                </p>

                <Separator />

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-2">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        disabled={isLoading}
                        variant="contained"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {isUploading
                                    ? t('uploading')
                                    : isEdit
                                      ? t('updating')
                                      : t('creating')}
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4" />
                                {isEdit
                                    ? t('regulations:update-regulation')
                                    : t('regulations:create-regulation')}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateEditRegulationModal
